import {
  LANGUAGE_EN,
  LANGUAGES_OPTIONS,
} from '@edulastic/constants/const/languages'
import produce from 'immer'
import {
  keyBy as _keyBy,
  shuffle as _shuffle,
  isEmpty,
  set,
  cloneDeep,
  get,
  isObject,
} from 'lodash'

import { isEnglishTestInstructionAvailable } from '../../common/utils/helpers'

// This method will mutate each language options and apply value from english to other language.
// Then shuffle other language options in the same order as english
const shuffleLanguageFeaturesOptions = (
  { languageFeatures, options },
  shuffledOptions
) => {
  if (!languageFeatures) return

  const languageKeys = Object.keys(languageFeatures)
  if (!languageKeys.length) return

  languageKeys.forEach((language) => {
    const question = languageFeatures[language]

    // question.options for additional language could be empty array if authored in english + other language
    // not application if only authored in additional language but not in english
    if (!question.options?.length) {
      question.options = cloneDeep(options)
    }

    question.options.forEach((option, index) => {
      if (options?.[index]) {
        option.value = options[index].value // apply value in other language options so that we can shuffle in same order
      }
    })
    const optionByValue = _keyBy(question.options, 'value')
    question.options = shuffledOptions.map((id) => optionByValue[id])
  })
}

const shuffleOptions = (
  questions = [],
  qActivitiesById = {},
  testItemId = ''
) => {
  const optionsOrder = {}

  const modifiedQuestions = questions.map((question) => {
    /**
     * questionKey is a composite key of testItemId and question.id
     * This is used to lookup the shuffled options in the qActivitiesById
     * and to store the shuffled options in the optionsOrder
     */
    const questionKey = `${testItemId}_${question.id}`
    if (question.type === 'multipleChoice') {
      let { shuffledOptions } = qActivitiesById[questionKey] || {}

      if (!shuffledOptions) {
        shuffledOptions = _shuffle(question.options.map((item) => item.value))
      }

      optionsOrder[questionKey] = shuffledOptions
      const optionByValue = _keyBy(question.options, 'value')
      shuffleLanguageFeaturesOptions(question, shuffledOptions)
      question.options = shuffledOptions.map((id) => optionByValue[id])
    }
    return question
  })

  return [modifiedQuestions, optionsOrder]
}

export const ShuffleChoices = (testItems, questionActivities) => {
  // Create a unique key using both qid and testItemId to ensure uniqueness
  const qActivitiesById = _keyBy(
    questionActivities,
    (activity) => `${activity.testItemId}_${activity.qid}`
  )
  let shuffles = {}
  testItems.forEach((item) => {
    let optionsOrder
    ;[item.data.questions, optionsOrder] = shuffleOptions(
      item.data.questions,
      qActivitiesById,
      item._id // Pass the test item id
    )
    shuffles = {
      ...shuffles,
      ...optionsOrder,
    }
  })

  return [testItems, shuffles]
}

/**
 * input
 * questions: [
 * {
 *  ...restProps,
 *  hints: [{label: "", value: ""}]
 * },
 * {
 *  ...restProps,
 *  hints: [{label: "", value: ""}]
 * }
 * ]
 *
 * output: a number >= 0
 *
 * logic:
 * for all questions, check if there are hints
 * for all hints check if the label is not empty
 * empty label is possible when a user entered something in the hint and then cleared it (obj is not removed)
 *
 * a number > 0 would indicate the current item has hints which have non empty label
 */

//  TODO :  need to remove the object if the hint is cleared

export const showHintButton = (questions) =>
  questions.reduce((acc, question) => {
    if (question.hints) {
      // handling cases when hints are undefined
      acc += question.hints.filter((hint) => hint.label.length > 0).length
    }
    return acc
  }, 0)

/**
 * @param {*} { isSectionAdaptiveTest, baseSectionIdAndIsBaseSectionSubmitted, autoSubmit }
 * This function is called on auto-submit when timer for a section ends,
 * used to determine whether its a section adaptive test and if base/common section
 * of adaptive test is submitted. If not submitted should submit base section
 * and navigate to section start page.
 * @returns a boolean that signifies if base section submitted or not
 */
export const shouldSubmitBaseSectionOnAutosubmitForSectionAdaptiveTest = ({
  isSectionAdaptiveTest = false,
  baseSectionIdAndIsBaseSectionSubmitted = {},
  autoSubmit = false,
}) => {
  const {
    isBaseSectionSubmitted,
    baseSectionId,
  } = baseSectionIdAndIsBaseSectionSubmitted
  if (
    autoSubmit &&
    isSectionAdaptiveTest &&
    !isEmpty(baseSectionIdAndIsBaseSectionSubmitted) &&
    !isEmpty(baseSectionId) &&
    typeof isBaseSectionSubmitted === 'boolean'
  ) {
    return !isBaseSectionSubmitted
  }
  return false
}

/**
 * @param {*} currentPageUrl
 * currentPageUrl - is the current url of the student test attempt.
 * currentPageUrl is of type /student/:assessmentType/:testId/class/:groupId/uta/:utaId/itemId/:itemId
 * This function will return section start page url,
 * that is of type /student/:assessmentType/:testId/class/:groupId/uta/:utaId/sections-start
 * @returns string - section start url
 */
const getSectionStartUrlFromTestAttemptUrl = (currentPageUrl = '') => {
  return `${currentPageUrl.slice(
    0,
    currentPageUrl.indexOf('itemId')
  )}sections-start`
}

/**
 * @param {*} { currentRouterState, currentPageUrl }
 * This function returns the current router state along with other data
 * that signifies that on closing timer end modal user needs to be navigated to section start page
 * and update the necessary router state.
 * @returns routerState object
 */
export const getSectionStartPageRouterStateData = ({
  currentRouterState,
  currentPageUrl,
}) => {
  const sectionsStartPageUrl = getSectionStartUrlFromTestAttemptUrl(
    currentPageUrl
  )
  return {
    ...currentRouterState,
    fromSummary: true,
    isBaseSectionAutoSubmitted: true,
    sectionsStartPageUrl,
  }
}

const testPatterns = [/instruction/, /instructionAcknowledgementText/]

const getAvailablePaths = (data, prev = '', paths = []) => {
  Object.keys(data).forEach((key) => {
    const path = prev + (prev ? '.' : '') + key
    if (isObject(data[key]) && key !== 'languageFeatures') {
      return getAvailablePaths(data[key], path, paths)
    }
    if (key !== 'languageFeatures') {
      paths.push(path)
    }
  })
  return paths
}

// get all the paths that match the patterns
export const getLanguageDataPaths = (patterns, data) => {
  const availablePaths = getAvailablePaths(data)
  return availablePaths.filter((path) =>
    patterns?.some((pattern) => new RegExp(pattern, 'g').test(path))
  )
}

// change the test data in the preferred language
// here we edit data in preferred language
// changes store data
export const changeTestDataInPreferredLanguage = (
  languageCode,
  prevTest,
  newTest,
  allowedToSelectMultiLanguage = false
) => {
  // If current language is not English, Updates languageFeature
  if (languageCode && languageCode !== LANGUAGE_EN) {
    const changedTest = produce(newTest, (draft) => {
      if (!draft.languageFeatures) {
        draft.languageFeatures = {}
      }

      if (!draft.languageFeatures[languageCode]) {
        draft.languageFeatures[languageCode] = {}
      }
      const dataFields = getLanguageDataPaths(testPatterns, newTest)
      dataFields.forEach((path) => {
        const currentData = get(newTest, path)
        // don't update empty stimulus to other languages
        if (path !== 'instruction' || currentData) {
          set(draft.languageFeatures[languageCode], path, currentData)
        }
        // use previous data for English
        // if it exist in previouse data.
        const prevData = get(prevTest, path)
        if (prevData) {
          set(draft, path, prevData)
        } else {
          set(draft, path, '')
        }
      })
    })

    return changedTest
  }

  // I clean existing languageData when EN data is updated
  if (
    (!languageCode || languageCode === LANGUAGE_EN) &&
    !allowedToSelectMultiLanguage
  ) {
    const changedData = produce(newTest, (draft) => {
      Object.keys(draft.languageFeatures).forEach((langKey) => {
        const langDataPaths = getAvailablePaths(draft.languageFeatures[langKey])
        const cleanLangData = {}
        langDataPaths.forEach((path) => {
          const enData = get(draft, path)
          const langData = get(draft.languageFeatures[langKey], path)
          if (enData && langData) {
            set(cleanLangData, path, langData)
          }
        })
        draft.languageFeatures[langKey] = cleanLangData
      })
    })
    return changedData
  }

  return newTest
}

// change the test data to the preferred language
// here we get data from the preferred language
// does not change store data
export const changeTestDataToPreferredLanguage = (
  testData = {},
  languageCode
) => {
  const { languageFeatures = {} } = testData
  if (
    !isEnglishTestInstructionAvailable(testData) &&
    !isEnglishTestInstructionAvailable(
      testData?.languageFeatures?.[languageCode]
    ) &&
    testData?.languageFeatures
  ) {
    // Changing english language to first languageFeatures language only when current language data is not available
    // Loop through the language codes and get the instruction of the first language that is available
    const languageKeys = LANGUAGES_OPTIONS.map((lang) => lang.value)
    for (const languageKey of languageKeys) {
      const languageInstruction =
        languageFeatures?.[languageKey]?.instruction ||
        languageFeatures?.[languageKey]?.instructionAcknowledgementText

      // If the instruction is available, then set the language and break the loop
      if (languageInstruction) {
        languageCode = languageKey
        break
      }
    }
  }
  if (
    LANGUAGE_EN !== languageCode &&
    testData?.languageFeatures?.[languageCode]
  ) {
    const languageData = testData.languageFeatures[languageCode]
    const languageDataPaths = getAvailablePaths(languageData)

    const changedData = produce(testData, (draft) => {
      languageDataPaths.forEach((path) => {
        const langData = get(languageData, path)
        set(draft, path, langData)
      })
    })

    return changedData
  }

  return testData
}

// reset the empty test language data
export const resetEmptyTestLanguageData = (data, language) => {
  const languageData = get(data, ['languageFeatures', language], {})
  let previousData = data
  if (
    !isEmpty(language) &&
    isEmpty(languageData) &&
    !isEmpty(data?.languageFeatures) &&
    !isEnglishTestInstructionAvailable(data)
  ) {
    const languageCodes = Object.keys(data.languageFeatures)
    const sortLanguageCodes = LANGUAGES_OPTIONS.filter((lang) =>
      languageCodes.includes(lang.value)
    )
    const firstLanguage = sortLanguageCodes[0].value
    if (firstLanguage !== language) {
      previousData = data.languageFeatures[firstLanguage]
    }
    const languageDataPaths = getAvailablePaths(previousData)
    const changedTest = produce(data, (draft) => {
      languageDataPaths.forEach((path) => {
        const langData = get(languageData, path)
        const endData = get(previousData, path)
        if (!langData) {
          set(draft, `languageFeatures.${language}.${path}`, endData)
        }
      })
    })
    return changedTest
  }
  return null
}

// reset the empty test english data
export const resetEmptyTestEnglishData = (data) => {
  const clonedTest = cloneDeep(data)
  if (
    !isEmpty(data?.languageFeatures) &&
    !isEnglishTestInstructionAvailable(data)
  ) {
    const languageCodes = Object.keys(data.languageFeatures)
    const sortLanguageCodes = LANGUAGES_OPTIONS.filter((lang) =>
      languageCodes.includes(lang.value)
    )

    const languageDataWithInstruction = sortLanguageCodes
      .map(({ value }) => value)
      .map((languageCode) => clonedTest.languageFeatures[languageCode])
      .find((languageContent) => languageContent?.instruction.trim()?.length)

    if (languageDataWithInstruction) {
      const changedTest = produce(data, (draft) => {
        const languageDataPaths = getAvailablePaths(languageDataWithInstruction)
        languageDataPaths.forEach((path) => {
          const langData = get(draft, path)
          const endData = get(languageDataWithInstruction, path)
          if (!langData) {
            set(draft, path, endData)
          }
        })
      })
      return changedTest
    }
    return null
  }
  return null
}

// return a new version of the item (newItem)
// where data related to languages not present in langauageTabs has been removed or cleared.
// It also returns a flag (omitted) indicating whether any data was actually removed.
export const omitEmptyTestData = (testData, langauageTabs) => {
  const newTest = { ...testData }
  let omitted = false
  if (
    isEnglishTestInstructionAvailable(newTest) &&
    !langauageTabs.includes(LANGUAGE_EN)
  ) {
    const dataFields = getLanguageDataPaths(testPatterns, newTest)
    dataFields.forEach((path) => {
      set(newTest, path, '')
    })

    omitted = true
  }
  if (newTest.languageFeatures) {
    newTest.languageFeatures = Object.keys(newTest.languageFeatures).reduce(
      (acc, key) => {
        if (langauageTabs.includes(key)) {
          acc[key] = newTest.languageFeatures[key]
        } else {
          omitted = true
        }
        return acc
      },
      {}
    )
  }
  return { newTest, omitted }
}

// get the primary language code
export const getPrimaryLanguageCode = ({
  instruction,
  instructionAcknowledgementText,
  languageFeatures,
} = {}) => {
  if (instruction || instructionAcknowledgementText) {
    return LANGUAGE_EN
  }

  let primaryLanguageCode = LANGUAGE_EN
  const languageCodes = LANGUAGES_OPTIONS.map((lang) => lang.value)
  for (const languageCode of languageCodes) {
    const languageInstruction =
      languageFeatures?.[languageCode]?.instruction ||
      languageFeatures?.[languageCode]?.instructionAcknowledgementText
    if (languageInstruction) {
      primaryLanguageCode = languageCode
      break
    }
  }

  return primaryLanguageCode
}

export const updateScratchPadDataWithItemHighlights = ({
  scratchPadData = {},
  attachments = [],
}) => {
  attachments.forEach((attachment) => {
    if (
      attachment?.type === 'testItemHighlights' &&
      attachment?.data?.itemHighlights
    ) {
      if (scratchPadData?.[attachment.referrerId2]) {
        scratchPadData[attachment.referrerId2] = {
          ...scratchPadData[attachment.referrerId2],
          itemHighlights: attachment.data.itemHighlights,
        }
      } else {
        scratchPadData[attachment.referrerId2] = {
          itemHighlights: attachment.data.itemHighlights,
        }
      }
    }
  })
  return scratchPadData
}
